import React, { useEffect, useRef, useState } from "react";
import { Typography } from "@material-ui/core";
import * as MaterialUI from "@material-ui/core";
import * as MaterialIcons from "@material-ui/icons";

import { PluginRuntime, i18nTranslator } from "@markedesk/plugin-sdk/ui";

import { mountPluginJsx } from "./pluginJsx";

/**
 * Hospeda o resultado de uma action desenhado pelo PRÓPRIO plugin.
 *
 * O core não decide como o resultado aparece — recebe o texto do endpoint e
 * entrega ao JSX que o plugin declarou em `resultJsx`. Antes existia um
 * `resultType: "suggestion"` com o balão desenhado aqui dentro: funcionava
 * para o AIA-Tools e engessava qualquer outro plugin, que receberia o balão
 * que o core tinha escolhido.
 *
 * O JSX é transpilado (Babel, sob demanda) e renderizado com o React DO APP,
 * então hooks funcionam e `props.mui` dá o Material-UI do app — o resultado
 * sai com a cara do sistema sem bundle próprio.
 *
 * Props entregues ao JSX do plugin:
 *   texto        — o texto do resultado (data.result)
 *   dados        — a resposta crua do endpoint, para o que mais o plugin mandou
 *   carregando   — a chamada ainda está em curso
 *   contexto     — ticketId, contactId, channel, whatsappId
 *   inserirTexto — põe o texto na caixa de digitação (quando o slot oferece)
 *   fechar       — descarta o resultado
 *   t            — tradutor dos textos do plugin (`provides.i18n`)
 *   idioma       — idioma da interface
 *   React, mui, icons
 */
export default function ResultadoJsx({
  jsx,
  pluginId,
  texto,
  dados,
  carregando,
  contexto,
  onInserirTexto,
  onFechar,
}) {
  const noRef = useRef(null);
  const montadoRef = useRef(null);
  const cacheRef = useRef({});
  const [erro, setErro] = useState(null);

  const props = {
    texto,
    // Resposta crua do endpoint. O core nao interpreta — o plugin sabe o que
    // colocou ali.
    dados,
    carregando,
    contexto,
    inserirTexto: onInserirTexto,
    fechar: onFechar,
    React,
    mui: MaterialUI,
    icons: MaterialIcons,
    /**
     * Idioma da interface AGORA ("pt", "en", "es"...).
     *
     * O core nao traduz o texto do plugin — nao conhece as frases dele. Passa
     * o idioma; o dicionario e do plugin. Sem isto, plugin so consegue falar
     * uma lingua, e a tela mistura o app em ingles com o balao em portugues.
     */
    idioma: PluginRuntime.idioma || "pt",
    /**
     * Tradutor ja amarrado a ESTE plugin e ao idioma atual: dentro do
     * componente se escreve `t("usarResposta")`.
     *
     * Os textos vem do `provides.i18n` do plugin, registrados no carregamento.
     * Antes, o jeito era serializar o dicionario inteiro dentro de cada string
     * de JSX — o mesmo texto viajava duplicado em cada componente.
     */
    t: i18nTranslator(pluginId, PluginRuntime.idioma),
  };

  useEffect(() => {
    let vivo = true;
    if (!jsx || !noRef.current) return undefined;

    mountPluginJsx(noRef.current, jsx, props, cacheRef.current)
      .then((montado) => {
        if (!vivo) {
          montado.unmount();
          return;
        }
        montadoRef.current = montado;
      })
      // JSX quebrado do plugin não pode derrubar a tela do chat — o erro
      // aparece no lugar do resultado e o atendimento segue.
      .catch((e) => vivo && setErro(e?.message || String(e)));

    return () => {
      vivo = false;
      if (montadoRef.current) {
        montadoRef.current.unmount();
        montadoRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jsx]);

  // Re-render com props novas SEM remontar: é o que faz o "carregando" virar
  // texto sem o componente do plugin perder o estado interno.
  useEffect(() => {
    if (montadoRef.current) montadoRef.current.render(props);
  });

  if (!jsx) {
    // Action declarou resultType "jsx" e esqueceu o `resultJsx`. Sem aviso, o
    // clique não faria nada e pareceria bug do sistema.
    return (
      <Typography variant="caption" color="error">
        Plugin declarou resultado em JSX mas não enviou o componente (`resultJsx`).
      </Typography>
    );
  }

  if (erro) {
    return (
      <Typography variant="caption" color="error">
        Falha ao renderizar o resultado do plugin: {erro}
      </Typography>
    );
  }

  return <div ref={noRef} />;
}
